import { useEffect, useState, type RefObject } from 'react';
import AdUnit from './AdUnit';

interface AdSidebarStackProps {
  slot: string | undefined;
  /** Ref to the main content column this sidebar sits next to; its height determines how many ad units get added. */
  targetRef?: RefObject<HTMLElement | null>;
}

const GAP_PX = 16;
const AD_HEIGHTS = [250, 600, 250, 600, 250, 600, 250];
const MIN_UNITS = 2;
const MAX_UNITS = 20;

/**
 * A sidebar ad column meant to span roughly the full scroll length of a long page.
 * Deliberately NOT sticky: a sticky unit only ever shows 1-2 ads per viewport and
 * looked glued to the header right below it. Plain stacked units scroll normally
 * with the page, and a ResizeObserver on `targetRef` (the main content column)
 * grows the stack to roughly match that height, so it keeps up whether the guide
 * is short or very long instead of a fixed count leaving blank space at the bottom.
 */
export default function AdSidebarStack({ slot, targetRef }: AdSidebarStackProps) {
  const [count, setCount] = useState(MIN_UNITS);

  useEffect(() => {
    const el = targetRef?.current;
    if (!el) return;

    const recompute = () => {
      const targetHeight = el.offsetHeight;
      let total = 0;
      let n = 0;
      while (total < targetHeight && n < MAX_UNITS) {
        total += AD_HEIGHTS[n % AD_HEIGHTS.length] + (n > 0 ? GAP_PX : 0);
        n++;
      }
      setCount(Math.max(n, MIN_UNITS));
    };

    recompute();
    const observer = new ResizeObserver(recompute);
    observer.observe(el);
    return () => observer.disconnect();
  }, [targetRef]);

  return (
    <div className="space-y-4">
      {Array.from({ length: count }, (_, i) => (
        <AdUnit key={i} slot={slot} label="Advertisement" style={{ minHeight: AD_HEIGHTS[i % AD_HEIGHTS.length] }} />
      ))}
    </div>
  );
}
